import React, { useCallback, useMemo, useState } from "react";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import { Box, Button, IconButton, Stack, StackProps, Typography } from "@mui/material";
import { ProgressOverlay } from "components/Progress-overlay/ProgressOverlay";
import { DocumentSectionType } from "constants/documents";
import dayjs from "dayjs";
import { useActionHook } from "hooks";
import {
  ChartType,
  CurrencyType,
  DateRangeValue,
  DocumentReportsSectionSettings,
  DocumentSectionGraph,
  IAxis,
  ReportFilters
} from "store/types";

import { reportSections, ReportSectionType } from "../Content/Content";
import { Graph } from "./Graph";
import { KPI } from "./KPI";
import { PickParametersSet } from "./PickParametersSet";
import { Queries } from "./Queries";
import { SelectSet } from "./SelectSet";
import { Table } from "./Table";

interface Props {
  sectionType?: ReportSectionType;
  forceCreateQueries: boolean;
  documentId: number;
  order: number;
  onSubmit: (data: Pick<State, "queries" | "pickFiltersSet"> & DocumentReportsSectionSettings) => void;
  onCancel: () => void;
}

const titles: Record<FormType, string> = {
  kpi: "KPI",
  graph: "Graph",
  table: "Table",
  cashflow_graph: "Cashflow",
  queries: "Report parameter set",
  pickFiltersSet: "Parameter set"
};

// The types weren't reused because of the code complexity, in case of reuse it becomes unreadable
export type State = {
  queries: {
    dateRange: DateRangeValue;
    currency: CurrencyType;
    filters: ReportFilters;
    exchangeMethod: "toDate" | "realDate";
  };
  pickFiltersSet: { isUseCurrent: boolean };
  kpi: { filter: string; title: string; id: string | number; isDeleted: boolean }[];
  graph: {
    chart_type: ChartType | null;
    group_by: string | null;
    measure: string | null;
    xAxis?: IAxis;
    yAxis?: IAxis;
    id: string | number;
    isDeleted: boolean;
  }[];
  table: {
    name: string;
    columns: { calcFields: string[]; characteristics: string[]; measures: string[] };
    group_by: string[];
    index_name: { name: string; index: string } | null;
    original_currency: boolean;
  };
  cashflow_graph: {
    set_id: number | null;
  };
};

type FormType = keyof State;
const maxSteps = 3;

const initialState: State = {
  kpi: [
    { filter: "", title: "", id: "1", isDeleted: false }
    // { filter: "", title: "", id: "2", isDeleted: false },
    // { filter: "", title: "", id: "3", isDeleted: false }
  ],
  graph: [
    { chart_type: null, group_by: null, measure: null, id: "1", isDeleted: false }
    // { chart_type: null, group_by: null, measure: null, id: "2", isDeleted: false }
  ],
  table: {
    name: "",
    columns: { calcFields: [], characteristics: [], measures: [] },
    group_by: [],
    index_name: null,
    original_currency: false
  },
  cashflow_graph: {
    set_id: null
  },
  queries: {
    dateRange: { value: "fromInception", to: dayjs().toISOString() },
    currency: "USD",
    filters: {},
    exchangeMethod: "toDate"
  },
  pickFiltersSet: { isUseCurrent: true }
};

type FormTypeProps<T extends keyof State = keyof State> = {
  data: State[T];
  setData: (cb: (prevState: State[T]) => State[T]) => void;
};

// "any" to let put components with different props - should be as FormType
const Forms: Record<FormType, React.FC<FormTypeProps<any>>> = {
  kpi: KPI,
  graph: Graph,
  table: Table,
  cashflow_graph: SelectSet as any, //temporary issue with types, because of the cashflow graph has different flow and it can update set_id and queries at the same time
  queries: Queries,
  pickFiltersSet: PickParametersSet
};

interface ContentProps extends Props {
  onSubmit: (data: Pick<State, "queries" | "pickFiltersSet"> & DocumentReportsSectionSettings) => void;
}

const stateToSettings = (state: State): Pick<State, "queries" | "pickFiltersSet"> & DocumentReportsSectionSettings => {
  return {
    queries: state.queries,
    pickFiltersSet: state.pickFiltersSet,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    kpi: state.kpi.filter(({ isDeleted }) => !isDeleted).map(({ id, isDeleted, ...rest }) => ({ ...rest })), // Remove excess fields
    graph: state.graph
      .filter(
        // Filter to remove deleted items and don't let submit if there are invalid data
        (
          g
        ): g is {
          chart_type: ChartType;
          group_by: string;
          measure: string;
          id: string | number;
          isDeleted: boolean;
        } =>
          g.chart_type !== null ||
          g.group_by !== null ||
          Boolean(g.chart_type !== "scatter" && !g.measure) ||
          !g.isDeleted
      )
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      .map(({ id, isDeleted, ...rest }) => ({ ...rest })) as DocumentSectionGraph["report_data"]["settings"], // Remove excess fields
    table: state.table,
    cashflow_graph: {
      set_id: state.cashflow_graph.set_id || 1
    }
  };
};

const Content: React.FC<ContentProps> = ({
  sectionType = DocumentSectionType.KPI,
  onCancel,
  onSubmit,
  forceCreateQueries
}) => {
  const [step, setStep] = useState<number>(1);
  const [state, setState] = useState<State>(initialState);

  const { formType, FormComponent } = useMemo<{
    formType: FormType;
    FormComponent: React.FC<FormTypeProps<any>>;
  }>(() => {
    if (step === 1) {
      return { formType: sectionType as FormType, FormComponent: Forms[sectionType as FormType] };
    }
    if (step === 2) {
      return { formType: "pickFiltersSet", FormComponent: PickParametersSet };
    }
    return { formType: "queries", FormComponent: Queries };
  }, [step]);

  const handleSetData = useCallback<(cb: (prevState: State[FormType]) => State[FormType]) => void>(
    (cb) => {
      return setState((prev) => ({ ...prev, [formType]: cb(prev[formType]) }));
    },
    [formType]
  );

  const handleSetQueriesData = useCallback<(cb: (prevState: State["queries"]) => State["queries"]) => void>((cb) => {
    return setState((prev) => ({ ...prev, queries: cb(prev.queries) }));
  }, []);

  const handleSubmit = () => {
    // For cashflow_graph, submit immediately after first step
    if (sectionType === DocumentSectionType.CashflowGraph) {
      return onSubmit(stateToSettings(state));
    }

    const nextStep = step + 1;

    // if last step, submit
    if (step === maxSteps) {
      return onSubmit(stateToSettings(state));
    }

    if (forceCreateQueries) {
      return setStep(3);
    }

    // Submit if we need skip filters step
    if (nextStep === maxSteps && state.pickFiltersSet.isUseCurrent) {
      return onSubmit(stateToSettings(state));
    }

    setStep(nextStep);
  };

  const onBack = () => {
    // For cashflow_graph, there's only one step, so no back button needed
    if (sectionType === DocumentSectionType.CashflowGraph) return;

    if (step === 1) return;

    if (step === 3 && forceCreateQueries) {
      return setStep(1);
    }

    setStep(step - 1);
  };

  const isDisabledSubmit = useMemo<boolean>(() => {
    // For cashflow_graph, validation is the same for all steps since there's only one step
    if (sectionType === DocumentSectionType.CashflowGraph) {
      return !state.cashflow_graph.set_id;
    }

    if (step > 1) return false;

    if (sectionType === DocumentSectionType.KPI) {
      return !state.kpi.length || state.kpi.some((kpi) => !kpi.filter || !kpi.title);
    }

    if (sectionType === DocumentSectionType.Graph) {
      return state.graph.some(
        (item) =>
          !item.chart_type ||
          !item.group_by ||
          (item.chart_type !== "scatter" && !item.measure) || // Disable not scatter chart if no measure selected
          (item.chart_type === "scatter" && !(item.xAxis && item.yAxis)) // Disable scatter is no x & y axis selected
      );
    }

    if (sectionType === DocumentSectionType.Table) {
      const { calcFields, characteristics, measures } = state.table.columns;
      const hasParameters =
        calcFields.length || characteristics.length || measures.length || Boolean(state.table.index_name);
      return state.table.name.length < 2 || !state.table.group_by.length || !hasParameters;
    }

    return false;
  }, [step, state, sectionType]);

  return (
    <>
      <Stack sx={{ flexDirection: "row", justifyContent: "space-between", borderBottom: "1px solid #DBE8F5" }}>
        <Typography sx={{ fontSize: "18px", fontWeight: 400, lineHeight: "24px" }}>{titles[formType]}</Typography>
        <IconButton onClick={onCancel}>
          <CloseIcon />
        </IconButton>
      </Stack>
      <Box sx={{ overflow: "auto", pt: "6px" }}>
        {FormComponent && (
          <FormComponent
            data={
              formType === DocumentSectionType.CashflowGraph
                ? { set_id: state.cashflow_graph.set_id, queries: state.queries }
                : state[formType]
            }
            setData={handleSetData}
            {...(formType === DocumentSectionType.CashflowGraph && { setQueriesData: handleSetQueriesData })}
          />
        )}
      </Box>
      <Stack sx={{ flexDirection: "row", justifyContent: "space-between" }}>
        {step > 1 && sectionType !== DocumentSectionType.CashflowGraph ? (
          <Button disabled={step === 1} startIcon={<ArrowBackIcon />} onClick={onBack}>
            Back
          </Button>
        ) : (
          <Box />
        )}
        <Stack sx={{ flexDirection: "row", justifyContent: "flex-end", gap: "8px" }}>
          <Button variant="outlined" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSubmit} disabled={isDisabledSubmit}>
            Confirm
          </Button>
        </Stack>
      </Stack>
    </>
  );
};

export const CreateReportSection: React.FC<Props> = (props) => {
  const { sectionType, onSubmit } = props;

  const { loading, action } = useActionHook(onSubmit);

  if (!sectionType || !reportSections.includes(sectionType)) {
    return null;
  }

  return (
    <ProgressOverlay containerSX={{ ...style }} inProgress={loading}>
      <Content {...props} onSubmit={action} />
    </ProgressOverlay>
  );
};

const style: StackProps["sx"] = {
  display: "flex",
  flexDirection: "column",
  outline: "none",
  bgcolor: "white",
  minWidth: 300,
  p: "32px",
  gap: "20px",
  borderRadius: "8px !important",
  boxShadow: "0px 0px 10px 0px rgba(0, 0, 0, 0.1)",
  maxHeight: "500px",
  overflow: "hidden"
};
